import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Calendar as CalIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("dd_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getWeekDates(offset = 0) {
  const now = new Date();
  now.setDate(now.getDate() + offset * 7);
  const start = new Date(now);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d.toISOString().split("T")[0];
  });
}

export default function ScheduleManager() {
  const [slots, setSlots] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "10:00" });
  const weekDates = getWeekDates(weekOffset);

  const fetchSlots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/schedule/slots?from=${weekDates[0]}&to=${weekDates[6]}`, auth());
      setSlots(res.data);
    } catch { /* ok */ }
  }, [weekDates[0], weekDates[6]]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const addSlot = async () => {
    if (!form.date || !form.start_time || !form.end_time) { toast.error("Fill all fields"); return; }
    try {
      await axios.post(`${API}/schedule/slots`, form, auth());
      toast.success("Time slot added");
      setForm({ ...form, date: "", start_time: "09:00", end_time: "10:00" });
      fetchSlots();
    } catch { toast.error("Failed to add slot"); }
  };

  const deleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/schedule/slots/${slotId}`, auth());
      toast.success("Slot removed");
      fetchSlots();
    } catch { toast.error("Failed to delete"); }
  };

  const slotsForDate = (date) => slots.filter((s) => s.date === date);

  return (
    <div className="p-6 sm:p-8" data-testid="schedule-manager">
      <h1 className="text-[#F8FAFC] font-black text-2xl tracking-tighter mb-6">Appointment Schedule</h1>

      {/* Add Slot Form */}
      <div className="border border-[#1F2937] rounded-sm p-5 bg-[#0F1A15] mb-6" data-testid="add-slot-form">
        <p className="text-[#64748B] text-xs uppercase tracking-wider mb-3">Add Available Time Slot</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Date</Label>
            <Input type="date" data-testid="slot-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0B110E] border-[#1F2937] text-[#F8FAFC] h-10 rounded-sm text-sm w-44 focus-visible:ring-[#D95D39]" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Start</Label>
            <Input type="time" data-testid="slot-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-[#0B110E] border-[#1F2937] text-[#F8FAFC] h-10 rounded-sm text-sm w-32 focus-visible:ring-[#D95D39]" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">End</Label>
            <Input type="time" data-testid="slot-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-[#0B110E] border-[#1F2937] text-[#F8FAFC] h-10 rounded-sm text-sm w-32 focus-visible:ring-[#D95D39]" />
          </div>
          <button onClick={addSlot} data-testid="add-slot-btn" className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-bold text-sm px-5 h-10 rounded-sm flex items-center gap-2 transition-colors duration-150">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeekOffset(weekOffset - 1)} className="text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150" data-testid="prev-week">&larr; Prev Week</button>
        <span className="text-[#F8FAFC] text-sm font-semibold flex items-center gap-2"><CalIcon className="w-4 h-4 text-[#D95D39]" />{weekDates[0]} — {weekDates[6]}</span>
        <button onClick={() => setWeekOffset(weekOffset + 1)} className="text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150" data-testid="next-week">Next Week &rarr;</button>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
        {weekDates.map((date, di) => {
          const daySlots = slotsForDate(date);
          const isToday = date === new Date().toISOString().split("T")[0];
          return (
            <div key={date} className={`border rounded-sm p-3 min-h-[140px] ${isToday ? "border-[#D95D39]/50 bg-[#D95D39]/5" : "border-[#1F2937] bg-[#0F1A15]"}`}>
              <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isToday ? "text-[#D95D39]" : "text-[#64748B]"}`}>{DAYS[di]}</p>
              <p className="text-[#F8FAFC] text-sm font-semibold mb-2">{date.split("-").slice(1).join("/")}</p>
              {daySlots.length === 0 && <p className="text-[#374151] text-xs italic">No slots</p>}
              {daySlots.map((slot) => (
                <div key={slot.id} className={`text-xs rounded-sm px-2 py-1.5 mb-1 flex items-center justify-between ${slot.is_booked ? "bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[#F59E0B]" : "bg-[#16A34A]/10 border border-[#16A34A]/20 text-[#16A34A]"}`} data-testid={`slot-${slot.id}`}>
                  <span className="font-semibold">{slot.start_time}-{slot.end_time}</span>
                  {!slot.is_booked && (
                    <button onClick={() => deleteSlot(slot.id)} className="hover:text-[#DC2626] transition-colors duration-150" data-testid={`delete-slot-${slot.id}`}><Trash2 className="w-3 h-3" /></button>
                  )}
                  {slot.is_booked && <span className="text-[10px]">BOOKED</span>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
